import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReturnDraft, saleReturnService } from '../../services/saleReturns/saleReturnService'
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler'

const invalidateReturnViews = (queryClient: ReturnType<typeof useQueryClient>, billId?: string) => {
  queryClient.invalidateQueries({ queryKey: ['bills'] })
  queryClient.invalidateQueries({ queryKey: ['sale-returns'] })
  queryClient.invalidateQueries({ queryKey: ['sale-return-eligibility'] })
  queryClient.invalidateQueries({ queryKey: ['credit-payments'] })
  queryClient.invalidateQueries({ queryKey: ['customers'] })
  queryClient.invalidateQueries({ queryKey: ['customer'] })
  queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  queryClient.invalidateQueries({ queryKey: ['inventory-stock'] })
  queryClient.invalidateQueries({ queryKey: ['stock-ledger'] })
  queryClient.invalidateQueries({ queryKey: ['monthly-performance'] })
  queryClient.invalidateQueries({ queryKey: ['ar-aging-report'] })
  queryClient.invalidateQueries({ queryKey: ['sales-tax-report'] })
  queryClient.invalidateQueries({ queryKey: ['advance-tax-report'] })
  queryClient.invalidateQueries({ queryKey: ['pnl-report'] })
  queryClient.invalidateQueries({ queryKey: ['balance-sheet-report'] })
  queryClient.invalidateQueries({ queryKey: ['account-ledger'] })
  queryClient.invalidateQueries({ queryKey: ['account-ledger-balance'] })
  if (billId) queryClient.invalidateQueries({ queryKey: ['bill', billId] })
}

export const useSaleReturnEligibility = (billId: string, enabled: boolean) => useQuery({
  queryKey: ['sale-return-eligibility', billId],
  queryFn: () => saleReturnService.getEligibility(billId),
  enabled: enabled && Boolean(billId),
})

export const useSaleReturns = (params: { bill_id?: string; customer_id?: string }) => useQuery({
  queryKey: ['sale-returns', params],
  queryFn: () => saleReturnService.list(params),
  enabled: Boolean(params.bill_id || params.customer_id),
})

export const useQuoteSaleReturn = () => useMutation({
  mutationFn: (draft: ReturnDraft) => saleReturnService.quote(draft),
  onError: showErrorToast,
})

export const useCreateSaleReturn = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (draft: ReturnDraft & { request_key: string; reason?: string; return_date?: string }) => saleReturnService.create(draft),
    onSuccess: (_, draft) => {
      invalidateReturnViews(queryClient, draft.bill_id)
      showSuccessToast('Sale return recorded successfully!')
    },
    onError: showErrorToast,
  })
}

export const useReverseSaleReturn = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string; billId: string }) => saleReturnService.reverse(id, reason),
    onSuccess: (_, variables) => {
      invalidateReturnViews(queryClient, variables.billId)
      showSuccessToast('Sale return reversed successfully!')
    },
    onError: showErrorToast,
  })
}
